import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { computeVibe, moodEmoji } from "@/lib/vibe";
import { colorOf } from "@/lib/constants";
import { X } from "lucide-react";

/**
 * MoodPeek — tiny popover that shows a member's last 7 days in mood.
 *
 * Triggered by long-press / right-click on a MemberCard in the
 * dashboard. Anchored to the card's bounding rect when provided;
 * falls back to screen-centred on mobile.
 *
 * Rendered one peek at a time at the page level so overflow on cards
 * and the dashboard layout don't clip it.
 */
export default function MoodPeek({ member, anchorRect, onClose }) {
  const [history, setHistory] = useState(null); // null = loading, [] = empty
  const [err, setErr] = useState(null);
  const popRef = useRef(null);

  // Build the 7-day row: today at right, 6 days back at left. Each day
  // gets the latest check-in from that day (history API returns newest
  // first, so the first match per day-key wins).
  useEffect(() => {
    let alive = true;
    setHistory(null);
    setErr(null);
    api
      .history(member.id, 7)
      .then((rows) => {
        if (!alive) return;
        setHistory(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!alive) return;
        setErr("Couldn't load recent moods");
        setHistory([]);
      });
    return () => {
      alive = false;
    };
  }, [member.id]);

  // Close on ESC or outside click.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Pick the mood for each of the last 7 calendar days.
  const days = (() => {
    const out = [];
    const now = new Date();
    const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today0);
      d.setDate(d.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
      out.push({ key, date: d, checkin: null });
    }
    if (Array.isArray(history)) {
      for (const c of history) {
        // Prefer the explicit `date` field (YYYY-MM-DD) the backend
        // already indexes by; fall back to parsing a timestamp.
        let k = c?.date;
        if (!k) {
          try {
            const dt = new Date(c.updated_at || c.created_at);
            k = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
              2,
              "0"
            )}-${String(dt.getDate()).padStart(2, "0")}`;
          } catch {
            continue;
          }
        }
        const slot = out.find((s) => s.key === k);
        // history returns newest-first — first hit wins per day
        if (slot && !slot.checkin) slot.checkin = c;
      }
    }
    return out;
  })();

  // Average mood for the whole week (excluding days with no check-in).
  const weekStats = (() => {
    const moods = days
      .map((d) => d.checkin?.mood)
      .filter((m) => typeof m === "number");
    if (!moods.length) return null;
    const avg = moods.reduce((a, b) => a + b, 0) / moods.length;
    return { avg: Math.round(avg * 10) / 10, logged: moods.length };
  })();

  // Position: prefer anchored above the card, else centre.
  const positionStyle = (() => {
    if (!anchorRect) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }
    const panelW = 320;
    const panelH = 200;
    const gap = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = anchorRect.left + anchorRect.width / 2 - panelW / 2;
    left = Math.max(12, Math.min(left, vw - panelW - 12));
    let top = anchorRect.top - panelH - gap;
    // Not enough room above → drop below the card.
    if (top < 12) top = Math.min(anchorRect.bottom + gap, vh - panelH - 12);
    return { top, left };
  })();

  const dayLabel = (date, i) => {
    if (i === 6) return "Today";
    if (i === 5) return "Yest";
    return date.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 2);
  };

  const color = colorOf(member.color);

  return (
    <div
      className="fixed inset-0 z-[90]"
      onClick={onClose}
      data-testid={`mood-peek-backdrop-${member.id}`}
      aria-hidden="false"
    >
      <div
        ref={popRef}
        onClick={(e) => e.stopPropagation()}
        className="absolute bg-[color:var(--surface)] border border-[color:var(--line)] rounded-xl shadow-2xl p-4 w-[320px] animate-in fade-in zoom-in-95 duration-150"
        style={{ ...positionStyle }}
        data-testid={`mood-peek-${member.id}`}
        role="dialog"
        aria-label={`${member.name}'s week in mood`}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-xl flex-shrink-0 overflow-hidden"
              style={{ background: color }}
              aria-hidden="true"
            >
              {member.avatar_image ? (
                <img
                  src={member.avatar_image}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                member.avatar_emoji
              )}
            </div>
            <div className="min-w-0">
              <div className="font-display text-base leading-tight truncate">
                {member.name}
              </div>
              <div className="label-tiny text-[color:var(--ink-3)]">
                7-day mood
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-7 w-7 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center -mt-1 -mr-1"
            data-testid={`mood-peek-close-${member.id}`}
            aria-label="Close mood peek"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        {/* 7-day strip */}
        <div
          className="grid grid-cols-7 gap-1"
          data-testid={`mood-peek-strip-${member.id}`}
        >
          {days.map((slot, i) => {
            const c = slot.checkin;
            const tint = c ? computeVibe(c).tint : null;
            const emoji = c ? moodEmoji(c.mood) : "·";
            const title = c
              ? `${slot.date.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                })} — mood ${c.mood}/10`
              : `${slot.date.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                })} — no check-in`;
            return (
              <div
                key={slot.key}
                className="flex flex-col items-center gap-1"
                title={title}
                data-testid={`mood-peek-day-${slot.key}`}
              >
                <div
                  className="w-full aspect-square rounded-lg border border-[color:var(--line-soft)] flex items-center justify-center text-lg"
                  style={{
                    background: tint || "var(--bg-soft)",
                    opacity: c ? 1 : 0.55,
                  }}
                  aria-label={title}
                >
                  <span className="leading-none">{emoji}</span>
                </div>
                <div
                  className={`text-[10px] leading-none ${
                    i === 6
                      ? "font-semibold text-[color:var(--ink)]"
                      : "text-[color:var(--ink-3)]"
                  }`}
                >
                  {dayLabel(slot.date, i)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer — average or empty / error states */}
        <div className="mt-3 text-[12px] leading-snug text-[color:var(--ink-2)]">
          {history === null && <span>Loading…</span>}
          {err && <span className="text-[color:var(--rough-strong)]">{err}</span>}
          {history && !err && weekStats && (
            <span>
              Logged <strong>{weekStats.logged}</strong> of 7 days · avg mood{" "}
              <strong>{weekStats.avg}</strong> / 10
            </span>
          )}
          {history && !err && !weekStats && (
            <span>No check-ins in the last 7 days yet.</span>
          )}
        </div>
      </div>
    </div>
  );
}
